/**
 * SplatEditSystem — public API for splat-level editing in the StorySplat editor.
 *
 * A session decodes the scene's splat (any format, including LOD streams and
 * LCC2 imports) into a CPU SplatEditModel, renders it with the edit shaders on
 * its own entity, and exposes SuperSplat-equivalent tools plus StorySplat
 * extras. Everything is undoable. Saving bakes the edits into plain gaussian
 * columns that the editor encodes into the scene's formats.
 *
 * Usage:
 *   const system = new SplatEditSystem(app, cameraEntity);
 *   await system.activate(splatEntity);
 *   await system.selectRect('set', { x1, y1, x2, y2 });
 *   system.deleteSelection();
 *   const baked = system.bake({ outputTransform });
 *   system.deactivate();
 */
import * as pc from 'playcanvas';
import { SplatEditModel, type SplatColumnSource, type SplatEditCounts } from './core/SplatEditModel';
import { SplatState, UNSELECTABLE, type SelectionOp } from './core/SplatState';
import { EditHistory } from './core/History';
import {
    type EditOp, StateOp, selectOp, selectAllOp, selectNoneOp, selectInvertOp,
    hideSelectionOp, unhideAllOp, deleteSelectionOp, restoreDeletedOp, isolateSelectionOp,
    editTargets, TransformSplatsOp, ColorGradeOp, DuplicateOp, MultiOp, CloneStampOp, mul4,
} from './core/ops';
import { Ranges } from './core/Ranges';
import { createGradeTerms, gradeTerms, type GradeParams, type GradeTerms } from './core/ColorGrade';
import {
    selectScreenRegion, rectRegion, maskRegion, polygonRegion, circleRegion,
    type ScreenContext, type ScreenRegion,
} from './core/selection/screen';
import { selectSphere, selectBox, selectCapsulePath, invertAffine } from './core/selection/volume';
import {
    computePropValues, computeHistogram, selectHistogramRange, selectColorMatch, computeDisplayColors,
    type SplatPropId, type PropContext, type HistogramResult, type PropValues,
} from './core/selection/props';
import {
    growSelection, shrinkSelection, robustCenter, selectConnectedSteps, analyseFloaters, analyseFloatersSteps, floaterMask, estimateSpacing, estimateSpacingSteps,
    type FloaterOptions, type FloaterAnalysis, type SpatialGrid,
} from './core/selection/spatial';
import { CloneStroke, buildCloneGrid } from './core/clone';
import { bakeEdits, type BakeOptions, type BakedSplats } from './core/bake';
import { EditRenderer, type EditDisplayOptions, DEFAULT_DISPLAY } from './render/EditRenderer';
import { SPLAT_EDIT_ACTIVE_EVENT } from './events';
import { ClonePreview } from './render/ClonePreview';
import { captureLayerAlpha, type AlphaImage } from './render/OpacityCapture';
import { SplatIdPicker, idAt, visibleIds, NO_SPLAT, type IdBuffer } from './render/SplatIdPicker';
import { PICK_OP_ALL, PICK_OP_SELECTED, PICK_OP_UNSELECTED } from './render/editShaders';
import { decodeGsplatResource, ColumnSource } from './io/decodeSource';
import { isOctreeResource, loadLodSource } from './io/loadLodSource';

export type { SelectionOp } from './core/SplatState';
export type { EditDisplayOptions } from './render/EditRenderer';
export type { GradeParams } from './core/ColorGrade';

export interface SplatEditStats extends SplatEditCounts {
    /** Number of SH bands in the edited data (0-3). */
    shBands: number;
}

export interface SplatEditChange {
    stats: SplatEditStats;
    canUndo: boolean;
    canRedo: boolean;
    undoName: string | null;
    redoName: string | null;
    dirty: boolean;
}

export interface ScreenSelectOptions {
    /** Only select splats that are visible (front-most) in the region. */
    depth?: boolean;
    /** Test each splat's projected footprint, scaled by this (0 = centers only). */
    footprint?: number;
}

export interface ActivateOptions {
    /** Pre-decoded columns (skips decoding the entity's resource). */
    source?: SplatColumnSource;
    /** Progress for slow sources (LOD streams decode many files). */
    onProgress?: (stage: string, fraction: number) => void;
    /** For LOD sources: load the finest level that fits this many splats. */
    maxLodSplats?: number;
    /** Extra entities to disable while editing (e.g. a GLB container). */
    hideEntities?: pc.Entity[];
    /** Parent and local transform for the edit entity (default: the source entity's). */
    parent?: pc.Entity;
    localTransform?: pc.Mat4;
    signal?: AbortSignal;
}

export interface SurfacePoint {
    index: number;
    position: pc.Vec3;
    depth: number;
}

export interface CloneStrokeOptions {
    /**
     * Keep the source → destination offset from the previous stroke, like an
     * image editor's "Aligned". Off (default): every stroke starts sampling at
     * the source point again, and the sample marker returns there on release.
     */
    aligned?: boolean;
    /** Delete the splats each dab paints over, so the copy replaces them. */
    replace?: boolean;
}

/** One brush sample: normalised screen point, radius in device pixels. */
export interface BrushSample {
    x: number;
    y: number;
    radius: number;
}

interface ActiveCloneStroke {
    ids: IdBuffer;
    aligned: boolean;
    replace: boolean;
    stroke: CloneStroke | null;
    preview: ClonePreview | null;
    builtAt: number;
    /** How long the last preview rebuild took (ms). */
    buildMs: number;
    timer: ReturnType<typeof setTimeout> | null;
    /** View depth the stroke paints at (from its last surface dab). */
    depth: number | null;
    /** How many of the stroke's covered splats are hidden so far (replace mode). */
    hiddenUpTo: number;
}

/** Rough memory a model of `numSplats` with `shBands` needs on the CPU (bytes). */
export function estimateEditMemory(numSplats: number, shBands: number): number {
    const floats = 14 + [0, 9, 24, 45][Math.max(0, Math.min(3, shBands))];
    // columns + state/index arrays + world caches + GPU staging copies
    return numSplats * (floats * 4 + 1 + 8 + 16) * 1.5;
}

export class SplatEditSystem {
    readonly app: pc.AppBase;
    readonly camera: pc.Entity;
    readonly history = new EditHistory();

    private _model: SplatEditModel | null = null;
    private _renderer: EditRenderer | null = null;
    private _picker: SplatIdPicker;
    private _sourceEntity: pc.Entity | null = null;
    private _hidden: { entity: pc.Entity; enabled: boolean }[] = [];
    private _queue: Promise<unknown> = Promise.resolve();
    private _environmentRange: [number, number] | null = null;
    private _sourceKind = 'unknown';
    private _spacing: number | null = null;
    private _lodLevel = 0;
    private _lodLevelCounts: number[] = [];
    private _floaterCache: { key: string; analysis: FloaterAnalysis } | null = null;
    private _cloneSource: pc.Vec3 | null = null;
    private _cloneOffset: pc.Vec3 | null = null;
    private _cloneCursor: pc.Vec3 | null = null;
    private _cloneGrid: { key: string; grid: SpatialGrid; centers: Float32Array } | null = null;
    private _cloneStroke: ActiveCloneStroke | null = null;

    onChange?: (change: SplatEditChange) => void;
    /** Back-compat: the previous API's callbacks. */
    onStatsChange?: (stats: SplatEditStats) => void;
    onUndoRedoChange?: (canUndo: boolean, canRedo: boolean) => void;

    constructor(app: pc.AppBase, camera: pc.Entity) {
        this.app = app;
        this.camera = camera;
        this._picker = new SplatIdPicker(app);
        this.history.onChange = () => this._notify();
    }

    // ─── Lifecycle ────────────────────────────────────────

    /** What kind of source the session decoded (ply, sog, spz, lod, ...). */
    get sourceKind(): string { return this._sourceKind; }

    /** LOD level being edited (0 = full detail) and every level's splat count. */
    get lodLevel(): number { return this._lodLevel; }
    get lodLevelCounts(): number[] { return this._lodLevelCounts.slice(); }

    /** Rows holding an LOD environment (kept separate on LOD save), or null. */
    get environmentRange(): [number, number] | null { return this._environmentRange; }

    get model(): SplatEditModel | null { return this._model; }
    get renderer(): EditRenderer | null { return this._renderer; }
    get sourceEntity(): pc.Entity | null { return this._sourceEntity; }

    isActive(): boolean {
        return !!this._model;
    }

    /** Back-compat alias used by older UI code paths. */
    getAttachment(): { state: Uint8Array; updateState: () => void } | null {
        const model = this._model;
        if (!model) return null;
        return { state: model.state, updateState: () => { model.markStateChanged(); this._notify(); } };
    }

    async activate(entity: pc.Entity, options: ActivateOptions = {}): Promise<void> {
        if (this._model) this.deactivate();
        const gsplat = entity.gsplat as unknown as { resource?: unknown; instance?: { resource?: unknown }; _placement?: { resource?: unknown } } | undefined;
        const resource = gsplat?.resource ?? gsplat?.instance?.resource ?? gsplat?._placement?.resource;

        let source = options.source ?? null;
        if (!source) {
            if (isOctreeResource(resource)) {
                options.onProgress?.('Loading full-detail LOD data', 0);
                const lod = await loadLodSource(this.app, resource, {
                    signal: options.signal,
                    maxSplats: options.maxLodSplats,
                    onProgress: (loaded, total) => options.onProgress?.('Loading full-detail LOD data', total ? loaded / total : 1),
                });
                source = lod.source;
                this._environmentRange = lod.environmentRange;
                this._lodLevel = lod.lodLevel;
                this._lodLevelCounts = lod.levelCounts;
                this._sourceKind = 'lod';
            } else {
                options.onProgress?.('Decoding splat', 0);
                source = await decodeGsplatResource(resource);
                this._sourceKind = describeResource(resource);
            }
        } else {
            this._sourceKind = 'provided';
        }
        if (!source) throw new Error('This splat format could not be decoded for editing.');
        if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

        options.onProgress?.('Preparing editor', 1);
        const model = new SplatEditModel(source);
        const cameraComponent = this.camera.camera;
        if (!cameraComponent) throw new Error('SplatEditSystem: camera entity has no camera component');

        this._sourceEntity = entity;
        const parent = (options.parent ?? entity.parent ?? this.app.root) as pc.Entity;
        this._renderer = new EditRenderer(this.app, model, {
            parent,
            localTransform: (options.localTransform ?? entity.getLocalTransform()).clone(),
            cameras: [cameraComponent],
        });
        this._model = model;
        this._spacing = null;
        this._resetSessionCaches();

        // hide the scene's own splat(s) for the session
        this._hidden = [entity, ...(options.hideEntities ?? [])].map((e) => ({ entity: e, enabled: e.enabled }));
        for (const h of this._hidden) h.entity.enabled = false;
        this.app.fire(SPLAT_EDIT_ACTIVE_EVENT, true);

        this.history.clear();
        this._notify();
    }

    /**
     * Edit several gsplat entities as one splat (e.g. a KHR GLB with multiple
     * primitives): each is decoded and baked into `parent` space, then merged.
     */
    async activateMany(entities: pc.Entity[], parent: pc.Entity, options: ActivateOptions = {}): Promise<void> {
        if (entities.length === 1) {
            await this.activate(entities[0], options);
            return;
        }
        const parentInv = new pc.Mat4().copy(parent.getWorldTransform()).invert();
        const parts: BakedSplats[] = [];
        for (let k = 0; k < entities.length; k++) {
            options.onProgress?.('Decoding splat', k / entities.length);
            const g = entities[k].gsplat as unknown as { resource?: unknown; instance?: { resource?: unknown } } | undefined;
            const decoded = await decodeGsplatResource(g?.resource ?? g?.instance?.resource);
            if (!decoded) throw new Error('A splat primitive could not be decoded for editing.');
            const rel = new pc.Mat4().mul2(parentInv, entities[k].getWorldTransform());
            parts.push(bakeEdits(new SplatEditModel(decoded), { outputTransform: rel.data }));
        }
        const shBands = Math.min(...parts.map((p) => p.shBands));
        const total = parts.reduce((s, p) => s + p.numSplats, 0);
        const names = [...parts.reduce((acc, p) => { for (const n of p.columns.keys()) acc.add(n); return acc; }, new Set<string>())]
            .filter((n) => !n.startsWith('f_rest_') || Number(n.slice(7)) < [0, 9, 24, 45][shBands]);
        const columns = new Map<string, Float32Array>();
        for (const name of names) {
            const col = new Float32Array(total);
            let at = 0;
            for (const p of parts) {
                // SH layout is per band count; re-index when a part has more bands than the merge keeps
                const src = p.columns.get(shBands === p.shBands || !name.startsWith('f_rest_') ? name : reindexSH(name, shBands, p.shBands));
                if (src) col.set(src, at);
                at += p.numSplats;
            }
            columns.set(name, col);
        }
        await this.activate(entities[0], {
            ...options,
            source: new ColumnSource(total, columns),
            parent,
            localTransform: new pc.Mat4(),
            hideEntities: [...entities.slice(1), ...(options.hideEntities ?? [])],
        });
        this._sourceKind = 'glb';
    }

    deactivate(): void {
        if (!this._model) return;
        try {
            this.cancelCloneStroke();
            this._resetSessionCaches();
            this.history.clear();
            this._renderer?.destroy();
        } finally {
            // Even if the teardown throws (e.g. a lost device): the scan, the clouds, the weather
            // and the backdrop must come back, and the session must end.
            this._renderer = null;
            for (const h of this._hidden) {
                if ((h.entity as unknown as { _destroyed?: boolean })._destroyed !== true) h.entity.enabled = h.enabled;
            }
            this._hidden = [];
            this.app.fire(SPLAT_EDIT_ACTIVE_EVENT, false);
            this._model = null;
            this._sourceEntity = null;
            this._environmentRange = null;
            this._lodLevel = 0;
            this._lodLevelCounts = [];
        }
    }

    /** Model versions restart with each session, so version-keyed caches must too. */
    private _resetSessionCaches(): void {
        this._floaterCache = null;
        this._cloneGrid = null;
        this._cloneSource = null;
        this._cloneOffset = null;
        this._cloneCursor = null;
    }

    destroy(): void {
        this.deactivate();
        this._picker.destroy();
    }

    // ─── Change notification ──────────────────────────────

    getStats(): SplatEditStats {
        const m = this._model;
        if (!m) return { total: 0, selected: 0, hidden: 0, deleted: 0, shBands: 0 };
        return { ...m.counts, shBands: m.shBands };
    }

    private _notify(): void {
        const stats = this.getStats();
        const h = this.history;
        this.onChange?.({ stats, canUndo: h.canUndo, canRedo: h.canRedo, undoName: h.undoName, redoName: h.redoName, dirty: h.dirty });
        this.onStatsChange?.(stats);
        this.onUndoRedoChange?.(h.canUndo, h.canRedo);
        this._renderer?.requestRender();
    }

    /** Run async work strictly in order (gestures, picks and ops never interleave). */
    private _enqueue<T>(fn: () => Promise<T> | T): Promise<T> {
        const run = this._queue.then(fn, fn);
        this._queue = run.catch(() => undefined);
        return run;
    }

    private _add(op: EditOp & { empty?: boolean }): boolean {
        // previews never outlive an edit (ops build their deltas before this), and a
        // clone stroke in progress was gathered against the model as it was
        this._clearMarks();
        this._dropCloneStroke();
        const added = this.history.add(op);
        if (!added) this._notify();
        return added;
    }

    // ─── Camera / screen context ──────────────────────────

    screenContext(): ScreenContext {
        const cam = this.camera.camera!;
        const device = this.app.graphicsDevice;
        const view = cam.viewMatrix;
        const proj = cam.projectionMatrix;
        const vp = new pc.Mat4().mul2(proj, view);
        return { viewProj: vp.data, view: view.data, proj: proj.data, width: device.width, height: device.height };
    }

    private _propContext(extra: Partial<PropContext> = {}): PropContext {
        const ctx = this.screenContext();
        const p = this.camera.getPosition();
        return { view: ctx.view, viewProj: ctx.viewProj, cameraPos: { x: p.x, y: p.y, z: p.z }, ...extra };
    }

    private _syncWorld(): void {
        this._renderer?.sync();
    }

    // ─── Screen-space selection ───────────────────────────

    selectRect(op: SelectionOp, rect: { x1: number; y1: number; x2: number; y2: number }, options: ScreenSelectOptions = {}): Promise<void> {
        return this._screenSelect(op, rectRegion(rect.x1, rect.y1, rect.x2, rect.y2), options, 'Rectangle Select');
    }

    selectPolygon(op: SelectionOp, points: { x: number; y: number }[], options: ScreenSelectOptions = {}): Promise<void> {
        return this._screenSelect(op, polygonRegion(points), options, 'Polygon Select');
    }

    /** Mask from a canvas: RGBA (stride 4, alpha channel) or single channel. */
    selectMask(
        op: SelectionOp,
        mask: { data: ArrayLike<number>; width: number; height: number; stride?: number; channel?: number },
        options: ScreenSelectOptions = {},
        name = 'Brush Select',
    ): Promise<void> {
        const region = maskRegion(mask.data, mask.width, mask.height, {
            stride: mask.stride ?? 4, channel: mask.channel ?? 3, threshold: 127,
        });
        return this._screenSelect(op, region, options, name);
    }

    private _screenSelect(op: SelectionOp, region: ScreenRegion, options: ScreenSelectOptions, name: string): Promise<void> {
        return this._enqueue(async () => {
            const model = this._model;
            if (!model) return;
            this._syncWorld();
            const ctx = this.screenContext();
            const footprint = options.footprint ?? 0;
            let mask: Uint8Array;
            if (options.depth) {
                // visible-only: splats front-most somewhere in the region
                const ids = await this._readIds(op, region);
                const visible = visibleIds(ids, (nx, ny) => region.contains(nx, ny));
                if (footprint > 0) {
                    mask = new Uint8Array(model.numSplats);
                    for (const id of visible) if (id < model.numSplats) mask[id] = 1;
                } else {
                    mask = selectScreenRegion(model, ctx, region, 0);
                    for (let i = 0; i < mask.length; i++) if (mask[i] && !visible.has(i)) mask[i] = 0;
                }
            } else {
                mask = selectScreenRegion(model, ctx, region, footprint);
            }
            this._add(selectOp(model, mask, op, name));
        });
    }

    private async _readIds(op: SelectionOp, region?: ScreenRegion): Promise<IdBuffer> {
        const renderer = this._renderer!;
        renderer.setPickOp(op === 'add' ? PICK_OP_UNSELECTED : op === 'remove' ? PICK_OP_SELECTED : PICK_OP_ALL);
        try {
            const r = region && region.maxX >= region.minX
                ? { x: region.minX, y: region.minY, w: region.maxX - region.minX, h: region.maxY - region.minY }
                : undefined;
            return await this._picker.read(this.camera.camera!, renderer.layer, r);
        } finally {
            renderer.setPickOp(PICK_OP_ALL);
        }
    }

    /** Pick the front-most splat under a normalised point; returns its index or null. */
    pickSplat(nx: number, ny: number, op: SelectionOp = 'set', tolerancePx = 3): Promise<number | null> {
        return this._enqueue(async () => {
            if (!this._model) return null;
            this._syncWorld();
            const ids = await this._readIds(op, circleRegion(nx, ny, tolerancePx + 1, this.app.graphicsDevice.width, this.app.graphicsDevice.height));
            const id = idAt(ids, nx, ny, tolerancePx);
            return id === NO_SPLAT || id >= this._model.numSplats ? null : id;
        });
    }

    /** Click select (SuperSplat picker). Clicking empty space with 'set' clears the selection. */
    async selectPoint(op: SelectionOp, nx: number, ny: number): Promise<number | null> {
        const id = await this.pickSplat(nx, ny, op);
        await this._enqueue(() => {
            const model = this._model;
            if (!model) return;
            if (id === null) {
                if (op === 'set') this._add(selectNoneOp(model));
                return;
            }
            const mask = new Uint8Array(model.numSplats);
            mask[id] = 1;
            this._add(selectOp(model, mask, op, 'Pick'));
        });
        return id;
    }

    /** World-space point on the visible surface under a normalised screen point. */
    async pickSurfacePoint(nx: number, ny: number, tolerancePx = 4): Promise<SurfacePoint | null> {
        const id = await this.pickSplat(nx, ny, 'set', tolerancePx);
        const model = this._model;
        if (id === null || !model) return null;
        const c = model.getWorldCenters();
        const position = new pc.Vec3(c[id * 3], c[id * 3 + 1], c[id * 3 + 2]);
        const depth = position.distance(this.camera.getPosition());
        return { index: id, position, depth };
    }

    // ─── Volume selection ─────────────────────────────────

    /** shapeToWorld maps the unit sphere (diameter 1) to world space. */
    selectSphere(op: SelectionOp, shapeToWorld: ArrayLike<number>, footprint = 0): Promise<void> {
        return this._enqueue(() => {
            const model = this._model;
            if (!model) return;
            this._syncWorld();
            this._add(selectOp(model, selectSphere(model, shapeToWorld, footprint), op, 'Sphere Select'));
        });
    }

    /** shapeToWorld maps the unit cube (side 1) to world space. */
    selectBox(op: SelectionOp, shapeToWorld: ArrayLike<number>, footprint = 0): Promise<void> {
        return this._enqueue(() => {
            const model = this._model;
            if (!model) return;
            this._syncWorld();
            this._add(selectOp(model, selectBox(model, shapeToWorld, footprint), op, 'Box Select'));
        });
    }

    /**
     * Sphere brush: stroke samples (normalised screen points with radii in
     * device pixels) are projected onto the visible surface; splats inside the
     * resulting 3D capsule path are selected. When `mask` is given and
     * footprint is 0, hits are limited to splats under the stroke on screen.
     */
    selectSphereBrush(
        op: SelectionOp,
        samples: { x: number; y: number; radius: number }[],
        options: { footprint?: number; mask?: { data: ArrayLike<number>; width: number; height: number; stride?: number; channel?: number } } = {},
    ): Promise<void> {
        return this._enqueue(async () => {
            const model = this._model;
            if (!model || samples.length === 0) return;
            this._syncWorld();
            const ids = await this._readIds('set');
            const centers = model.getWorldCenters();
            const path: number[] = [];
            let prev: { x: number; y: number; z: number; r: number } | null = null;
            for (const s of samples) {
                const dab = this._surfaceDab(ids, centers, s);
                if (!dab) { prev = null; continue; }
                const { x: px, y: py, z: pz, r } = dab;
                const starts = !prev || Math.hypot(prev.x - px, prev.y - py, prev.z - pz) > Math.max(prev.r, r) * 2;
                path.push(px, py, pz, starts ? -r : r);
                prev = dab;
            }
            if (!path.length) return;
            const footprint = options.footprint ?? 0;
            const maskRegionObj = options.mask
                ? maskRegion(options.mask.data, options.mask.width, options.mask.height, { stride: options.mask.stride ?? 4, channel: options.mask.channel ?? 3 })
                : undefined;
            const hit = selectCapsulePath(model, path, { footprint, screen: this.screenContext(), mask: maskRegionObj });
            this._add(selectOp(model, hit, op, 'Sphere Brush'));
        });
    }

    /**
     * Project a brush sample onto the visible surface: the world center of the
     * splat under it and the brush radius in world units at that depth.
     */
    private _surfaceDab(ids: IdBuffer, centers: Float32Array, s: BrushSample): { x: number; y: number; z: number; r: number } | null {
        const model = this._model!;
        const id = idAt(ids, s.x, s.y, Math.max(2, Math.round(s.radius * 0.25)));
        if (id === NO_SPLAT || id >= model.numSplats) return null;
        const x = centers[id * 3], y = centers[id * 3 + 1], z = centers[id * 3 + 2];
        return { x, y, z, r: this._worldRadius(s.radius, x, y, z) };
    }

    /** World size of `pixelRadius` device pixels at a world point. */
    private _worldRadius(pixelRadius: number, x: number, y: number, z: number): number {
        const cam = this.camera.camera!;
        const ortho = cam.projection === pc.PROJECTION_ORTHOGRAPHIC;
        const height = this.app.graphicsDevice.height;
        // world size of one pixel at unit depth
        const pixelScale = ortho
            ? (cam.orthoHeight * 2) / height
            : (2 * Math.tan((cam.fov * Math.PI / 180) / 2)) / height;
        const camPos = this.camera.getPosition();
        return pixelRadius * pixelScale * (ortho ? 1 : Math.hypot(x - camPos.x, y - camPos.y, z - camPos.z));
    }

    /** Distance of a world point in front of the camera, along its view direction. */
    private _viewDepth(x: number, y: number, z: number): number {
        const p = this.camera.getPosition();
        const f = this.camera.forward;
        return (x - p.x) * f.x + (y - p.y) * f.y + (z - p.z) * f.z;
    }

    /** The point under a normalised screen point at a given view depth. */
    private _rayAtViewDepth(nx: number, ny: number, depth: number): pc.Vec3 {
        const cam = this.camera.camera!;
        const { width, height } = this.app.graphicsDevice.clientRect;
        const near = cam.screenToWorld(nx * width, ny * height, cam.nearClip);
        const dir = cam.screenToWorld(nx * width, ny * height, cam.farClip).sub(near).normalize();
        const f = this.camera.forward;
        const along = dir.dot(f);
        const t = along > 1e-6 ? (depth - this._viewDepth(near.x, near.y, near.z)) / along : 0;
        return near.add(dir.mulScalar(t));
    }

    // ─── Colour-based selection ───────────────────────────

    /** Eyedropper: pick a splat and select every splat with a similar colour. */
    selectColorMatch(op: SelectionOp, nx: number, ny: number, threshold: number): Promise<boolean> {
        return this._enqueue(async () => {
            const model = this._model;
            if (!model) return false;
            this._syncWorld();
            const ids = await this._readIds('set');
            const id = idAt(ids, nx, ny, 3);
            if (id === NO_SPLAT || id >= model.numSplats) return false;
            const p = this.camera.getPosition();
            this._add(selectOp(model, selectColorMatch(model, id, threshold, { x: p.x, y: p.y, z: p.z }), op, 'Colour Match'));
            return true;
        });
    }

    /**
     * Flood select (SuperSplat "flood"): fill outward from a screen point over
     * pixels whose front-most splat has a colour within `threshold` of the
     * clicked one, then select through that screen mask with the usual
     * depth / footprint options. Works on the pick buffer, so it behaves the
     * same on WebGL2 and WebGPU.
     */
    selectFlood(
        op: SelectionOp, nx: number, ny: number, threshold: number,
        options: ScreenSelectOptions & { match?: 'opacity' | 'colour' } = {},
    ): Promise<boolean> {
        return this._enqueue(async () => {
            const model = this._model;
            const renderer = this._renderer;
            if (!model || !renderer) return false;
            this._syncWorld();
            const t = Math.max(0, Math.min(1, threshold));
            let buf: IdBuffer | null = null;
            let mask: Uint8Array | null;
            let w: number, h: number;
            if ((options.match ?? 'opacity') === 'opacity') {
                // SuperSplat: flood the rendered image by opacity (clicking empty space takes the faint fringe)
                const img = await captureLayerAlpha(this.app, this.camera.camera!, renderer.layer, () => renderer.requestRender());
                mask = alphaFloodMask(img, nx, ny, t);
                w = img.width; h = img.height;
            } else {
                buf = await this._readIds('set');
                const p = this.camera.getPosition();
                const colors = computeDisplayColors(model, { x: p.x, y: p.y, z: p.z });
                mask = floodMask(buf, colors, nx, ny, t);
                w = buf.w; h = buf.h;
            }
            if (!mask) return false;
            const region = maskRegion(mask, w, h, { stride: 1, channel: 0, threshold: 0 });
            const ctx = this.screenContext();
            const footprint = options.footprint ?? 0;
            let hit: Uint8Array;
            if (options.depth) {
                buf ??= await this._readIds('set');
                const visible = visibleIds(buf, (x, y) => region.contains(x, y));
                hit = footprint > 0 ? new Uint8Array(model.numSplats) : selectScreenRegion(model, ctx, region, 0);
                if (footprint > 0) { for (const id of visible) if (id < model.numSplats) hit[id] = 1; }
                else { for (let i = 0; i < hit.length; i++) if (hit[i] && !visible.has(i)) hit[i] = 0; }
            } else {
                hit = selectScreenRegion(model, ctx, region, footprint);
            }
            this._add(selectOp(model, hit, op, 'Flood Select'));
            return true;
        });
    }

    // ─── Data panel ───────────────────────────────────────

    computeHistogram(prop: SplatPropId, options: { onScreenOnly?: boolean; logBins?: boolean; numBins?: number } = {}): HistogramResult & { values: PropValues } | null {
        const model = this._model;
        if (!model) return null;
        this._syncWorld();
        const pv = computePropValues(model, prop, this._propContext({ onScreenOnly: options.onScreenOnly, logBins: options.logBins }));
        return { ...computeHistogram(model, pv, options.numBins ?? 256), values: pv };
    }

    /** Select splats in histogram bins [binStart, binEnd] (from a computeHistogram result). */
    selectHistogramRange(op: SelectionOp, hist: HistogramResult & { values: PropValues }, binStart: number, binEnd: number): Promise<void> {
        return this._enqueue(() => {
            const model = this._model;
            if (!model) return;
            this._add(selectOp(model, selectHistogramRange(hist.values, hist, binStart, binEnd), op, 'Select by Range'));
        });
    }

    // ─── Whole-selection ops ──────────────────────────────

    selectAll(): void { if (this._model) this._add(selectAllOp(this._model)); }
    selectNone(): void { if (this._model) this._add(selectNoneOp(this._model)); }
    selectInvert(): void { if (this._model) this._add(selectInvertOp(this._model)); }
    hideSelection(): void { if (this._model) this._add(hideSelectionOp(this._model)); }
    unhideAll(): void { if (this._model) this._add(unhideAllOp(this._model)); }
    deleteSelection(): void { if (this._model) this._add(deleteSelectionOp(this._model)); }
    restoreDeleted(): void { if (this._model) this._add(restoreDeletedOp(this._model)); }
    /** Back-compat alias. */
    resetDeleted(): void { this.restoreDeleted(); }
    isolateSelection(): void { if (this._model) this._add(isolateSelectionOp(this._model)); }

    /** Duplicate the selection in place; the copies become the selection. */
    duplicateSelection(): void {
        if (this._model) this._add(new DuplicateOp(this._model));
    }

    /** Indices of the selected live splats: a snapshot to act on after async work. */
    selectedRows(): Uint32Array {
        const model = this._model;
        if (!model) return new Uint32Array(0);
        const state = model.state;
        const rows: number[] = [];
        for (let i = 0; i < model.numSplats; i++) {
            if ((state[i] & (SplatState.selected | SplatState.deleted)) === SplatState.selected) rows.push(i);
        }
        return Uint32Array.from(rows);
    }

    /** Delete exactly these rows (the live ones) in one undo step. */
    deleteRows(rows: ArrayLike<number>, name = 'Delete'): void {
        const model = this._model;
        if (!model) return;
        const hit = new Uint8Array(model.numSplats);
        for (let k = 0; k < rows.length; k++) if (rows[k] < hit.length) hit[rows[k]] = 1;
        this._add(new StateOp(model, name, (s, i) => (hit[i] && !(s & SplatState.deleted)
            ? (s | SplatState.deleted) & ~SplatState.selected : s)));
    }

    /** Select a mask computed elsewhere (e.g. segments). */
    selectByMask(op: SelectionOp, mask: ArrayLike<number>, name = 'Select'): void {
        if (this._model) this._add(selectOp(this._model, mask, op, name));
    }

    // ─── 3D extras (beyond SuperSplat) ────────────────────

    /** Typical distance between neighbouring splats (cached per session). */
    typicalSpacing(): number {
        if (!this._model) return 0;
        if (this._spacing === null) {
            this._syncWorld();
            this._spacing = estimateSpacing(this._model);
        }
        return this._spacing;
    }

    /** typicalSpacing() worked out in slices that hand the thread back (null if the session closed meanwhile). */
    private async _typicalSpacingAsync(): Promise<number | null> {
        const model = this._model;
        if (!model) return null;
        if (this._spacing !== null) return this._spacing;
        this._syncWorld();
        const spacing = await this._drive(estimateSpacingSteps(model), model);
        if (spacing === null) return null;
        this._spacing ??= spacing;
        return this._spacing;
    }

    /**
     * Run resumable steps in ~30 ms slices, handing the thread back between
     * them so the viewport keeps rendering and the panel can show progress.
     * Resolves null when the session closes (or reopens) in the meantime.
     */
    private async _drive<T>(steps: Generator<void, T, void>, model: SplatEditModel): Promise<T | null> {
        let slice = performance.now();
        let r = steps.next();
        while (!r.done) {
            if (performance.now() - slice > 30) {
                await new Promise((resolve) => setTimeout(resolve, 0));
                if (this._model !== model) return null;
                slice = performance.now();
            }
            r = steps.next();
        }
        return r.value;
    }

    growSelection(radius?: number): void {
        const model = this._model;
        if (!model) return;
        this._syncWorld();
        const r = radius ?? this.typicalSpacing() * 2;
        this._add(selectOp(model, growSelection(model, r), 'add', 'Grow Selection'));
    }

    shrinkSelection(radius?: number): void {
        const model = this._model;
        if (!model) return;
        this._syncWorld();
        const r = radius ?? this.typicalSpacing() * 2;
        this._add(selectOp(model, shrinkSelection(model, r), 'set', 'Shrink Selection'));
    }

    /**
     * 3D magic wand from the splat under a screen point. The radius is
     * `radius`, or `radiusFactor` × the typical spacing (default 3), worked out
     * here rather than by the caller so the first use's spacing estimate also
     * runs inside the queued, busy step. A flood across a whole large scan
     * takes seconds, so it runs in ~30 ms slices that hand the thread back.
     */
    selectConnectedAt(
        op: SelectionOp, nx: number, ny: number,
        options: { radius?: number; radiusFactor?: number; colorTolerance?: number } = {},
    ): Promise<boolean> {
        return this._enqueue(async () => {
            const model = this._model;
            if (!model) return false;
            this._syncWorld();
            const ids = await this._readIds('set');
            const id = idAt(ids, nx, ny, 3);
            if (id === NO_SPLAT || id >= model.numSplats) return false;
            const p = this.camera.getPosition();
            const spacing = options.radius === undefined ? await this._typicalSpacingAsync() : 0;
            if (spacing === null) return false;
            const radius = options.radius ?? spacing * (options.radiusFactor ?? 3);
            this._syncWorld();
            const mask = await this._drive(selectConnectedSteps(model, [id], radius, {
                colorTolerance: options.colorTolerance, cameraPos: { x: p.x, y: p.y, z: p.z },
            }), model);
            if (!mask) return false;
            this._add(selectOp(model, mask, op, '3D Magic Wand'));
            return true;
        });
    }

    /** Select automatically detected floaters (small disconnected clusters / isolated splats). */
    selectFloaters(op: SelectionOp = 'set', options: FloaterOptions = {}): { selected: number; radius: number; clusters: number } {
        const model = this._model;
        if (!model) return { selected: 0, radius: 0, clusters: 0 };
        const result = this._floaters(options);
        this._add(selectOp(model, result.mask, op, 'Select Floaters'));
        return { selected: result.count, radius: result.radius, clusters: result.clusters };
    }

    /**
     * Floaters at these settings, never counting the environment (an LCC sky is
     * sparse and far away by design, and would otherwise read as floaters).
     */
    private _floaters(options: FloaterOptions): ReturnType<typeof floaterMask> {
        const result = floaterMask(this._floaterAnalysis(options.radius), options);
        const env = this._environmentRange;
        if (env) {
            for (let i = env[0]; i < env[1] && i < result.mask.length; i++) {
                if (result.mask[i]) { result.mask[i] = 0; result.count--; }
            }
        }
        return result;
    }

    /**
     * The thresholds-independent half of floater detection, cached while the
     * splats don't change, so re-previewing at another strength is instant.
     */
    private _floaterAnalysis(radius?: number): FloaterAnalysis {
        const model = this._model!;
        this._syncWorld();
        const r = radius ?? this.typicalSpacing() * 3;
        const key = this._floaterKey(model, r);
        if (this._floaterCache?.key !== key) this._floaterCache = { key, analysis: analyseFloaters(model, r) };
        return this._floaterCache.analysis;
    }

    /**
     * Run floater detection now, in slices that hand the thread back (it takes
     * seconds on a big scan), so the next preview / select / remove at any
     * strength is instant. Resolves false if the session closed meanwhile.
     */
    prepareFloaters(radius?: number): Promise<boolean> {
        return this._enqueue(async () => {
            const model = this._model;
            if (!model) return false;
            const spacing = radius === undefined ? await this._typicalSpacingAsync() : 0;
            if (spacing === null || this._model !== model) return false;
            const r = radius ?? spacing * 3;
            this._syncWorld();
            const key = this._floaterKey(model, r);
            if (this._floaterCache?.key === key) return true;
            const analysis = await this._drive(analyseFloatersSteps(model, r), model);
            if (!analysis) return false;
            this._floaterCache = { key, analysis };
            return true;
        });
    }

    private _floaterKey(model: SplatEditModel, radius: number): string {
        return `${model.rowsVersion}:${model.transformVersion}:${model.liveVersion}:${radius}`;
    }

    /**
     * Whether floater detection is cached for the splats as they are now, so
     * previewing at another strength is instant (detection on a big splat
     * takes seconds).
     */
    floaterAnalysisReady(radius?: number): boolean {
        const model = this._model;
        if (!model || !this._floaterCache) return false;
        return this._floaterCache.key === this._floaterKey(model, radius ?? this.typicalSpacing() * 3);
    }

    /**
     * Tint the floaters removeFloaters() would delete at these settings (a
     * preview: no undo step, cleared by the next edit). Returns the count.
     */
    previewFloaters(options: FloaterOptions = {}): { count: number; clusters: number } {
        const model = this._model;
        if (!model) return { count: 0, clusters: 0 };
        const result = this._floaters(options);
        model.setMarked(result.count ? result.mask : null);
        this._renderer?.requestRender();
        return { count: result.count, clusters: result.clusters };
    }

    /** Delete floaters in one undo step; returns how many. */
    removeFloaters(options: FloaterOptions = {}): number {
        this.previewFloaters(options);
        return this.deleteMarked('Remove Floaters');
    }

    /** Splats currently tinted by a preview. */
    get markedCount(): number {
        return this._model?.markedCount ?? 0;
    }

    /** Clear a preview tint without changing anything. */
    clearPreview(): void {
        this._clearMarks();
    }

    private _clearMarks(): void {
        if (this._model?.markedCount) {
            this._model.setMarked(null);
            this._renderer?.requestRender();
        }
    }

    /** Delete exactly the splats a preview tinted, in one undo step; returns how many. */
    deleteMarked(name = 'Remove'): number {
        const model = this._model;
        const count = model?.markedCount ?? 0;
        if (!model || !count) return 0;
        this._add(new StateOp(model, name, (s) => ((s & SplatState.marked) && !(s & UNSELECTABLE)
            ? (s | SplatState.deleted) & ~SplatState.selected : s)));
        return count;
    }

    /** Select the splats a preview tinted (to review them); returns how many. */
    selectMarked(op: SelectionOp = 'set', name = 'Select'): number {
        const model = this._model;
        const count = model?.markedCount ?? 0;
        if (!model || !count) return 0;
        const state = model.state;
        const mask = new Uint8Array(model.numSplats);
        for (let i = 0; i < mask.length; i++) mask[i] = state[i] & SplatState.marked ? 1 : 0;
        this._add(selectOp(model, mask, op, name));
        return count;
    }

    /** Keep only what's inside a box/sphere: deletes everything outside it in one undo step. */
    cropTo(shape: 'box' | 'sphere', shapeToWorld: ArrayLike<number>): void {
        const model = this._model;
        if (!model) return;
        this._syncWorld();
        const inside = shape === 'box' ? selectBox(model, shapeToWorld) : selectSphere(model, shapeToWorld);
        const outside = new StateOp(model, 'Crop', (s, i) => (s & (SplatState.hidden | SplatState.deleted) || inside[i]
            ? s : (s | SplatState.deleted) & ~SplatState.selected));
        this._add(outside);
    }

    // ─── Clone stamp (beyond SuperSplat) ──────────────────

    /** World point the clone stamp copies from; null until one is set. */
    get cloneSource(): pc.Vec3 | null {
        return this._cloneSource?.clone() ?? null;
    }

    /**
     * Where the clone stamp samples: the source point, the point a stroke is
     * copying from while it paints, and after an Aligned stroke the last point
     * it copied from (the next stroke continues from there).
     */
    get cloneSampleCursor(): pc.Vec3 | null {
        return (this._cloneCursor ?? this._cloneSource)?.clone() ?? null;
    }

    get isCloneStroking(): boolean {
        return !!this._cloneStroke;
    }

    /** Set the clone source (world space). A new source restarts the offset. */
    setCloneSource(position: pc.Vec3 | null): void {
        this._cloneSource = position ? position.clone() : null;
        this._cloneOffset = null;
        this._cloneCursor = null;
    }

    /** Set the clone source to the visible surface under a screen point (Alt+click). */
    async setCloneSourceAt(nx: number, ny: number): Promise<pc.Vec3 | null> {
        const hit = await this.pickSurfacePoint(nx, ny);
        if (hit) this.setCloneSource(hit.position);
        return hit ? hit.position.clone() : null;
    }

    /**
     * Start a clone stamp stroke. The id buffer is read once: the camera can't
     * move while the stroke owns the left button. Resolves false without a source.
     */
    beginCloneStroke(options: CloneStrokeOptions = {}): Promise<boolean> {
        return this._enqueue(async () => {
            if (!this._model || !this._cloneSource) return false;
            this._dropCloneStroke();
            this._clearMarks();
            // the stroke's grid needs the spacing; work it out here, in slices, not on the first dab
            if (await this._typicalSpacingAsync() === null || !this._cloneSource) return false;
            this._syncWorld();
            const ids = await this._readIds('set');
            this._cloneStroke = {
                ids, aligned: options.aligned ?? false, replace: !!options.replace,
                stroke: null, preview: null, builtAt: 0, buildMs: 0, timer: null, depth: null, hiddenUpTo: 0,
            };
            return true;
        });
    }

    /** Add brush samples to the stroke; resolves with how many splats they newly copy. */
    addCloneSamples(samples: BrushSample[]): Promise<number> {
        return this._enqueue(() => {
            const cs = this._cloneStroke;
            const model = this._model;
            const source = this._cloneSource;
            if (!cs || !model || !source) return 0;
            const centers = model.getWorldCenters();
            let added = 0;
            const coveredBefore = cs.stroke?.covered.length ?? 0;
            for (const s of samples) {
                const dab = this._cloneDab(cs, centers, s);
                if (!dab) continue;
                if (!cs.stroke) {
                    if (!cs.aligned || !this._cloneOffset) {
                        this._cloneOffset = new pc.Vec3(dab.x - source.x, dab.y - source.y, dab.z - source.z);
                    }
                    const grid = this._cloneGridFor(dab.r);
                    cs.stroke = new CloneStroke(grid.grid, grid.centers, this._cloneOffset, cs.replace);
                }
                added += cs.stroke.addDab(dab.x, dab.y, dab.z, dab.r);
                const o = cs.stroke.offset;
                this._cloneCursor = new pc.Vec3(dab.x - o.x, dab.y - o.y, dab.z - o.z);
            }
            if (added || (cs.stroke?.covered.length ?? 0) > coveredBefore) this._updateClonePreview(cs);
            return added;
        });
    }

    /**
     * Destination of a clone dab: the surface under the brush, unless there is
     * none or it jumps in depth (a hole, the background through it, something
     * in front). Then the stroke keeps painting at its current depth, or at the
     * source's depth before it has touched a surface, so holes can be filled.
     */
    private _cloneDab(cs: ActiveCloneStroke, centers: Float32Array, s: BrushSample): { x: number; y: number; z: number; r: number } | null {
        const hit = this._surfaceDab(cs.ids, centers, s);
        if (hit) {
            const d = this._viewDepth(hit.x, hit.y, hit.z);
            if (cs.depth === null || Math.abs(d - cs.depth) <= Math.max(hit.r * 3, Math.abs(cs.depth) * 0.1)) {
                cs.depth = d;
                return hit;
            }
        }
        const source = this._cloneSource;
        const depth = cs.depth ?? (source ? this._viewDepth(source.x, source.y, source.z) : null);
        if (depth === null || !(depth > 0)) return null;
        const p = this._rayAtViewDepth(s.x, s.y, depth);
        return { x: p.x, y: p.y, z: p.z, r: this._worldRadius(s.radius, p.x, p.y, p.z) };
    }

    /**
     * Commit the stroke as one undo step; resolves with the splats added and
     * painted over. The stroke's preview stays on screen until the result
     * draws, so releasing the brush never blanks or flickers the splat.
     */
    endCloneStroke(): Promise<{ added: number; replaced: number }> {
        return this._enqueue(() => {
            const cs = this._cloneStroke;
            const model = this._model;
            this._dropCloneStroke(true);
            // not aligned: the next stroke samples at the source again, so show it there
            if (cs && !cs.aligned) this._cloneCursor = null;
            if (!cs?.stroke || !model) {
                model?.clearPreviewHidden();
                return { added: 0, replaced: 0 };
            }
            // before the op records its state delta: the hide bit is display-only
            model.clearPreviewHidden();
            const o = cs.stroke.offset;
            const move = this.worldToModelTransform(new pc.Mat4().setTranslate(o.x, o.y, o.z));
            const op = new CloneStampOp(model, cs.stroke.sources, move, cs.stroke.covered);
            this._add(op);
            return { added: op.added, replaced: op.replaced };
        });
    }

    /** Abandon the stroke in progress (nothing is added). */
    cancelCloneStroke(): void {
        if (this._cloneStroke && !this._cloneStroke.aligned) this._cloneCursor = null;
        this._dropCloneStroke();
    }

    /**
     * Forget the stroke in progress. `keepPreview` (a commit) leaves its preview
     * drawn until the renderer shows the result; otherwise the preview goes and
     * what the stroke hid is drawn again.
     */
    private _dropCloneStroke(keepPreview = false): void {
        const cs = this._cloneStroke;
        if (!cs) return;
        this._cloneStroke = null;
        if (cs.timer) clearTimeout(cs.timer);
        const preview = cs.preview;
        const renderer = this._renderer;
        if (keepPreview && preview && renderer) renderer.whenRedrawn(() => preview.destroy());
        else preview?.destroy();
        if (!keepPreview) this._model?.clearPreviewHidden();
        renderer?.requestRender();
    }

    /** Grid over the live splats with a cell near the brush size (reused across strokes). */
    private _cloneGridFor(radius: number): { grid: SpatialGrid; centers: Float32Array } {
        const model = this._model!;
        const floor = this.typicalSpacing() * 2;
        const cell = 2 ** Math.ceil(Math.log2(Math.max(radius, floor, 1e-6)));
        const key = `${model.rowsVersion}:${model.transformVersion}:${model.liveVersion}:${cell}`;
        if (this._cloneGrid?.key !== key) this._cloneGrid = { key, ...buildCloneGrid(model, cell) };
        return this._cloneGrid;
    }

    /**
     * Rebuild the stroke preview (a trailing update catches up). Each rebuild
     * bakes every splat the stroke has copied so far into a new GPU resource,
     * so it gets slower as the stroke grows: the next one waits at least 4×
     * as long as the last took (and 100 ms), keeping a long stroke from
     * stalling the editor.
     */
    private _updateClonePreview(cs: ActiveCloneStroke): void {
        const model = this._model;
        const renderer = this._renderer;
        if (!cs.stroke || !model || !renderer || this._cloneStroke !== cs) return;
        const now = performance.now();
        const wait = Math.max(100, cs.buildMs * 4);
        if (now - cs.builtAt < wait) {
            cs.timer ??= setTimeout(() => { cs.timer = null; this._updateClonePreview(cs); }, wait - (now - cs.builtAt));
            return;
        }
        cs.builtAt = now;
        // replace mode: what the stroke paints over disappears with the copies' preview
        if (cs.replace) {
            model.hidePreview(cs.stroke.covered, cs.hiddenUpTo);
            cs.hiddenUpTo = cs.stroke.covered.length;
        }
        if (cs.stroke.sources.length) {
            const o = cs.stroke.offset;
            const baked = bakeEdits(model, {
                rows: cs.stroke.sources,
                outputTransform: this.worldToModelTransform(new pc.Mat4().setTranslate(o.x, o.y, o.z)),
            });
            // the overlay layer, so a pick read (edit layer only) never sees the preview
            cs.preview ??= new ClonePreview(this.app, renderer.entity, renderer.overlayLayer);
            cs.preview.update(baked);
        }
        renderer.requestRender();
        cs.buildMs = performance.now() - now;
    }

    // ─── Transforms ───────────────────────────────────────

    /** Entity world matrix the model is drawn with. */
    private _entityWorld(): pc.Mat4 {
        return this._renderer!.entity.getWorldTransform();
    }

    /** Convert a world-space transform to the model space used by palettes. */
    worldToModelTransform(world: pc.Mat4): Float32Array {
        const E = this._entityWorld();
        const Einv = new pc.Mat4().copy(E).invert();
        const out = new pc.Mat4().mul2(Einv, world).mul(E);
        return Float32Array.from(out.data);
    }

    /**
     * Apply a world-space transform (e.g. a gizmo delta about a pivot) to the
     * selection, or to every live splat when nothing is selected.
     */
    transformSelection(worldTransform: pc.Mat4, name = 'Transform'): void {
        const model = this._model;
        if (!model || !this._renderer) return;
        this._renderer.setPreviewTransform(null);
        const targets = editTargets(model);
        const op = new TransformSplatsOp(model, targets, this.worldToModelTransform(worldTransform));
        (op as { name: string }).name = name;
        this._add(op);
    }

    /** Live preview of a pending world-space transform on the selection (null clears). */
    setPreviewTransform(worldTransform: pc.Mat4 | null): void {
        const model = this._model;
        const renderer = this._renderer;
        if (!model || !renderer) return;
        if (!worldTransform) { renderer.setPreviewTransform(null); return; }
        renderer.setPreviewTransform(this.worldToModelTransform(worldTransform), !this.hasSelection());
    }

    /** Whether transforms currently target a selection (true) or the whole splat (false). */
    hasSelection(): boolean {
        return (this._model?.counts.selected ?? 0) > 0;
    }

    /** World-space bounds of the selection (or of all live splats when nothing is selected). */
    getBounds(selectionOnly = true): { min: pc.Vec3; max: pc.Vec3; center: pc.Vec3 } | null {
        const model = this._model;
        if (!model) return null;
        this._syncWorld();
        const c = model.getWorldCenters();
        const state = model.state;
        const useSel = selectionOnly && this.hasSelection();
        let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        for (let i = 0; i < model.numSplats; i++) {
            const s = state[i];
            if (s & SplatState.deleted) continue;
            if (useSel && !(s & SplatState.selected)) continue;
            const x = c[i * 3], y = c[i * 3 + 1], z = c[i * 3 + 2];
            if (!Number.isFinite(x + y + z)) continue;
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
            if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
        }
        if (minX === Infinity) return null;
        const min = new pc.Vec3(minX, minY, minZ);
        const max = new pc.Vec3(maxX, maxY, maxZ);
        return { min, max, center: new pc.Vec3().add2(min, max).mulScalar(0.5) };
    }

    private _boundsCache: { key: string; value: { min: pc.Vec3; max: pc.Vec3; center: pc.Vec3 } | null } | null = null;

    /**
     * Bounds of the selection (or everything when nothing is selected), cached
     * until the selection, a transform or the row count changes. Cheap enough
     * to call every frame for overlays.
     */
    getBoundsCached(): { min: pc.Vec3; max: pc.Vec3; center: pc.Vec3 } | null {
        const m = this._model;
        if (!m) return null;
        const key = `${m.stateVersion}:${m.transformVersion}:${m.rowsVersion}`;
        if (this._boundsCache?.key !== key) this._boundsCache = { key, value: this.getBounds(true) };
        return this._boundsCache.value;
    }

    private _centerCache: { model: SplatEditModel; key: string; value: pc.Vec3 | null } | null = null;

    /**
     * Robust center of the selection (or of every live splat when nothing is
     * selected), where the transform pivot goes: see robustCenter. Cached
     * until the selection, a transform or the rows change.
     */
    getSelectionCenter(): pc.Vec3 | null {
        const m = this._model;
        if (!m) return null;
        const key = `${m.stateVersion}:${m.transformVersion}:${m.rowsVersion}`;
        if (this._centerCache?.model !== m || this._centerCache.key !== key) {
            this._syncWorld();
            const c = robustCenter(m, true);
            this._centerCache = { model: m, key, value: c ? new pc.Vec3(c[0], c[1], c[2]) : null };
        }
        return this._centerCache.value?.clone() ?? null;
    }

    /**
     * Orient: rotate everything (or the selection) so the plane through three
     * world points becomes horizontal (normal → +Y), pivoting about p1, and
     * optionally move p1 to y = 0 (SuperSplat "Align to Grid").
     */
    alignPlane(p1: pc.Vec3, p2: pc.Vec3, p3: pc.Vec3, options: { dropToGround?: boolean; normalTowards?: pc.Vec3 } = {}): void {
        const a = new pc.Vec3().sub2(p2, p1);
        const b = new pc.Vec3().sub2(p3, p1);
        const n = new pc.Vec3().cross(a, b);
        if (n.length() < 1e-12) return;
        n.normalize();
        // make the normal face the camera side so "up" is the visible side of the surface
        const towards = options.normalTowards ?? this.camera.getPosition();
        if (new pc.Vec3().sub2(towards, p1).dot(n) < 0) n.mulScalar(-1);
        // like SuperSplat: a plane within 3° of one of the splat's own axes snaps to it
        const W = this._entityWorld();
        const axes = [W.getX(new pc.Vec3()), W.getY(new pc.Vec3()), W.getZ(new pc.Vec3())];
        n.copy(snapToAxis(n, axes, ORIENT_SNAP_DEG));
        const rot = rotationBetween(n, pc.Vec3.UP);
        const T = pivotTransform(p1, rot, 1);
        if (options.dropToGround) {
            const drop = new pc.Mat4().setTranslate(0, -p1.y, 0);
            T.mul2(drop, T);
        }
        this.transformSelection(T, 'Orient');
    }

    /** Measure: uniformly scale everything (or the selection) about `pivot`. */
    scaleAbout(pivot: pc.Vec3, factor: number, name = 'Rescale'): void {
        if (!(factor > 0) || !Number.isFinite(factor)) return;
        this.transformSelection(pivotTransform(pivot, new pc.Quat(), factor), name);
    }

    // ─── Colour ───────────────────────────────────────────

    /** Bake a grade into the selection (or the whole splat). Composes with existing grades. */
    applyColor(params: GradeParams): void {
        const model = this._model;
        if (!model) return;
        this._renderer?.setPreviewGrade(null);
        this._add(new ColorGradeOp(model, editTargets(model), gradeTerms(params, createGradeTerms())));
    }

    /** Remove grades from the selection (or the whole splat). */
    resetColor(): void {
        const model = this._model;
        if (!model) return;
        this._add(new ColorGradeOp(model, editTargets(model), null));
    }

    /** Live preview of the colour panel on the selection, or on everything when nothing is selected. */
    setPreviewColor(params: GradeParams | null): void {
        const renderer = this._renderer;
        if (!renderer) return;
        renderer.setPreviewGrade(params ? gradeTerms(params, createGradeTerms()) : null, !this.hasSelection());
    }

    // ─── Display ──────────────────────────────────────────

    setDisplay(options: Partial<EditDisplayOptions>): void {
        this._renderer?.setDisplay(options);
    }

    getDisplay(): EditDisplayOptions {
        return this._renderer?.display ?? { ...DEFAULT_DISPLAY };
    }

    // ─── History ──────────────────────────────────────────

    undo(): void { this._clearMarks(); this._dropCloneStroke(); this.history.undo(); }
    redo(): void { this._clearMarks(); this._dropCloneStroke(); this.history.redo(); }
    get canUndo(): boolean { return this.history.canUndo; }
    get canRedo(): boolean { return this.history.canRedo; }

    // ─── Save ─────────────────────────────────────────────

    /**
     * Local transform the session renders the data with (the scene splat's
     * transform when editing started). Combine with the target frame to build
     * `outputTransform` for bake().
     */
    get sourceLocalTransform(): pc.Mat4 {
        return this._renderer?.baseLocalTransform ?? new pc.Mat4();
    }

    /**
     * Matrix that re-expresses the data for an entity that will be drawn with
     * `targetLocal` instead of the current local transform, so the saved splat
     * appears exactly where it is now.
     */
    frameFixFor(targetLocal: pc.Mat4): Float32Array {
        const inv = new pc.Mat4().copy(targetLocal).invert();
        return Float32Array.from(new pc.Mat4().mul2(inv, this.sourceLocalTransform).data);
    }

    /**
     * World-space version of frameFixFor: re-express the data for an entity
     * whose world transform will be `targetWorld` (e.g. the viewer's
     * getPrimarySplatFrame().plyWorldTransform), so a reloaded save lands
     * exactly where the edited splat is drawn now.
     */
    frameFixForWorld(targetWorld: ArrayLike<number>): Float32Array {
        const renderer = this._renderer;
        if (!renderer) return Float32Array.from(new pc.Mat4().data);
        const target = new pc.Mat4().set(Array.from(targetWorld)).invert();
        return Float32Array.from(target.mul(renderer.entity.getWorldTransform()).data);
    }

    bake(options: BakeOptions = {}): BakedSplats {
        const model = this._model;
        if (!model) throw new Error('SplatEditSystem not active');
        return bakeEdits(model, options);
    }

    /** Back-compat: serialize edits to a binary PLY (uncompressed). */
    async serializeToPly(maxSHBands?: number, outputTransform?: ArrayLike<number>): Promise<Uint8Array> {
        const { encodePly, concatChunks } = await import('./core/writers');
        return concatChunks(encodePly(this.bake({ maxSHBands, outputTransform })));
    }

    /** Mark the current state as saved (dirty flag resets). */
    markSaved(): void {
        this.history.markSaved();
        this._notify();
    }
}

/**
 * 4-connected flood over the pick buffer from a normalised point. Pixels are
 * compared by the display colour of their front-most splat against the seed
 * colour (per-channel absolute difference). Returns a w*h coverage mask
 * (255 = filled) or null when the seed pixel shows no splat.
 */
export function floodMask(buf: IdBuffer, colors: Float32Array, nx: number, ny: number, threshold: number): Uint8Array | null {
    const { w, h, ids } = buf;
    const sx = Math.floor(nx * buf.width) - buf.x;
    const sy = Math.floor(ny * buf.height) - buf.y;
    if (sx < 0 || sy < 0 || sx >= w || sy >= h) return null;
    const seedId = ids[sy * w + sx];
    if (seedId === NO_SPLAT) return null;
    const r0 = colors[seedId * 3], g0 = colors[seedId * 3 + 1], b0 = colors[seedId * 3 + 2];
    const mask = new Uint8Array(w * h);
    const accept = (id: number) => {
        if (id === NO_SPLAT || id * 3 + 2 >= colors.length) return false;
        return Math.abs(colors[id * 3] - r0) <= threshold
            && Math.abs(colors[id * 3 + 1] - g0) <= threshold
            && Math.abs(colors[id * 3 + 2] - b0) <= threshold;
    };
    const stack = [sy * w + sx];
    mask[sy * w + sx] = 255;
    while (stack.length) {
        const k = stack.pop()!;
        const x = k % w, y = (k - x) / w;
        const visit = (q: number) => { if (!mask[q] && accept(ids[q])) { mask[q] = 255; stack.push(q); } };
        if (x > 0) visit(k - 1);
        if (x < w - 1) visit(k + 1);
        if (y > 0) visit(k - w);
        if (y < h - 1) visit(k + w);
    }
    return mask;
}

/** Orient snaps a picked plane to the splat's nearest axis within this angle (SuperSplat's value). */
export const ORIENT_SNAP_DEG = 3;

/** `n` (unit) snapped to the closest of `axes` (either sign) when within `maxDeg`; otherwise `n`. */
export function snapToAxis(n: pc.Vec3, axes: pc.Vec3[], maxDeg: number): pc.Vec3 {
    let best = Math.cos((maxDeg * Math.PI) / 180);
    let snapped: pc.Vec3 | null = null;
    for (const a of axes) {
        const axis = a.clone().normalize();
        const d = n.dot(axis);
        if (Math.abs(d) > best) {
            best = Math.abs(d);
            snapped = axis.mulScalar(Math.sign(d));
        }
    }
    return snapped ?? n.clone();
}

/**
 * SuperSplat's flood fill: 4-connected pixels whose opacity is within
 * `threshold` (0..1) of the clicked pixel's. 255 inside, 0 outside.
 */
export function alphaFloodMask(img: AlphaImage, nx: number, ny: number, threshold: number): Uint8Array | null {
    const { width: w, height: h, alpha } = img;
    const sx = Math.floor(nx * w), sy = Math.floor(ny * h);
    if (sx < 0 || sy < 0 || sx >= w || sy >= h) return null;
    const a0 = alpha[sy * w + sx];
    const limit = threshold * 255;
    const mask = new Uint8Array(w * h);
    const stack = [sy * w + sx];
    mask[sy * w + sx] = 255;
    while (stack.length) {
        const k = stack.pop()!;
        const x = k % w, y = (k - x) / w;
        const visit = (q: number) => { if (!mask[q] && Math.abs(alpha[q] - a0) < limit) { mask[q] = 255; stack.push(q); } };
        if (x > 0) visit(k - 1);
        if (x < w - 1) visit(k + 1);
        if (y > 0) visit(k - w);
        if (y < h - 1) visit(k + w);
    }
    return mask;
}

/** f_rest name in a `fromBands` layout for the same channel/coefficient as `name` in a `toBands` layout. */
function reindexSH(name: string, toBands: number, fromBands: number): string {
    const per = [0, 3, 8, 15];
    const k = Number(name.slice(7));
    const channel = Math.floor(k / per[toBands]);
    const coeff = k % per[toBands];
    return `f_rest_${channel * per[fromBands] + coeff}`;
}

function describeResource(resource: unknown): string {
    const r = resource as { gsplatData?: { isSog?: boolean; isCompressed?: boolean; positions?: unknown } } | null;
    const d = r?.gsplatData;
    if (!d) return 'unknown';
    if (d.positions) return 'spz';
    if (d.isSog) return 'sog';
    if (d.isCompressed) return 'compressed-ply';
    return 'ply';
}

function rotationBetween(from: pc.Vec3, to: pc.Vec3): pc.Quat {
    const f = from.clone().normalize();
    const t = to.clone().normalize();
    const d = f.dot(t);
    if (d > 0.999999) return new pc.Quat();
    if (d < -0.999999) {
        let axis = new pc.Vec3().cross(pc.Vec3.RIGHT, f);
        if (axis.length() < 1e-6) axis = new pc.Vec3().cross(pc.Vec3.UP, f);
        axis.normalize();
        return new pc.Quat().setFromAxisAngle(axis, 180);
    }
    const axis = new pc.Vec3().cross(f, t).normalize();
    return new pc.Quat().setFromAxisAngle(axis, Math.acos(Math.max(-1, Math.min(1, d))) * 180 / Math.PI);
}

/** T(pivot) · R · S · T(-pivot) */
export function pivotTransform(pivot: pc.Vec3, rotation: pc.Quat, scale: number | pc.Vec3): pc.Mat4 {
    const s = typeof scale === 'number' ? new pc.Vec3(scale, scale, scale) : scale;
    const m = new pc.Mat4().setTRS(pivot, rotation, s);
    const back = new pc.Mat4().setTranslate(-pivot.x, -pivot.y, -pivot.z);
    return m.mul(back);
}

// Re-exports for consumers that build their own tools
export { SplatState, Ranges, MultiOp, mul4, invertAffine };
export type { BakedSplats, BakeOptions, HistogramResult, SplatPropId, IdBuffer, GradeTerms };
